import { H2 } from '@triumph/design-system';
import { map } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DocumentCardHeader,
    DocumentCardTitle,
    DocumentPreviews,
    DownloadAllLink,
} from './DocumentCard.StyledComponents';
import DocumentPreview from '../DocumentPreview';
import { DetailDocument, DocumentsCardProps } from './DocumentsCard.types';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { getInvoiceDocuments, getInvoiceDocumentsDownloadUrl } from '@/app/shared/api/endpoints/SearchInvoiceDocuments';
import { StyledCard } from '@/app/features/common/DetailStyles';

const DocumentsCard: React.FC<DocumentsCardProps> = (props) => {
    const { breakpoint } = useAppContext();
    const { t } = useTranslation('invoicesDashboard');
    const [isDownloading, setIsDownloading] = useState(false);

    const {
        data: documentData,
        refetch: fetchDocuments,
    } = getInvoiceDocuments({
        assetId: props.assetId
    });

    useEffect(() => {
        fetchDocuments();
    }, []);

    const documentCardTitle = t('invoiceDocuments.documentCardHeader');
    const documentCardCount = documentData?.length || 0;

    const handleDownloadAllClick = async () => {
        if (isDownloading) return;

        setIsDownloading(true);
        try {
            const downloadUrl = await getInvoiceDocumentsDownloadUrl({ assetId: props.assetId });
            const anchor = document.createElement('a');
            anchor.href = downloadUrl;
            anchor.target = '_blank';
            anchor.click();
        } catch (error) {
            console.error('Failed to download all documents:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <StyledCard>
            <DocumentCardHeader>
                <DocumentCardTitle>
                    <H2>{documentCardTitle}</H2>
                    {`(${documentCardCount})`}
                </DocumentCardTitle>
                {documentCardCount > 0 && (
                    <DownloadAllLink
                        onClick={handleDownloadAllClick}
                        data-testid="download-all-link"
                    >
                        {isDownloading ? 'Loading...' : t('invoiceDocuments.downloadAll')}
                    </DownloadAllLink>
                )}
            </DocumentCardHeader>
            <DocumentPreviews breakpoint={breakpoint}>
                {map(
                    documentData,
                    (document: DetailDocument, index: number) => {
                        return (
                            <DocumentPreview
                                filename={document.filename}
                                documentUrl={document.url}
                                documentUploadDate={document.uploadedDate}
                                docType={document.docType}
                                key={`key-${document.documentId}`}
                                documentPreviewTestId={`document-preview-container-${index}`}
                                detailType={props.detailType}
                            />
                        );
                    }
                )}
            </DocumentPreviews>
        </StyledCard>
    );
};

export default DocumentsCard;
